import * as IoC from 'typescript-ioc';
import {
    ISalesforceAccountDTO,
    ISalesforceAccountSearchDTO,
    ISalesforceAccountSearchParams,
    ISalesforceUserDTO,
    ISalesforceUserSearchDTO,
    ISalesforceUserSearchParams,
    ISalesforceOpportunityDTO,
    ISalesforceOpportunitySearchDTO,
    ISalesforceOpportunitySearchParams,
    IRecentOrderDTO,
    IRecentOrderSearchDTO,
    IRecentOrderSearchParams,
    ISalesforceOrderCreateParams,
    ISalesforceOrderCreatePayload,
    ISalesforceOrderCreateResult,
    ISalesforceOrderDAO,
    ISalesforceOrderDTO,
    ISalesforceOrderFormDataDTO,
    ISalesforceOrderFormDataRequestParams,
    ISalesforceOrderRequestParams,
    ISalesforceOrderLinkParams,
    ISalesforceOrderLinkPayload,
    ISalesforceOrderUpdateParams,
    ISalesforceOrderUpdatePayload,
    ISalesforcePriceBookProductsDTO,
    ISalesforcePriceBookProductsParams,
} from '../salesforce-order.dao';
import { IApiClient } from '../../infrastructure/api-client';
import { ILocalStorageService, ILocalStorageResponse } from '../../infrastructure/local-storage-service';
import {
    SALESFORCE_ACCOUNT_SEARCH_URL,
    SALESFORCE_BUSINESS_LEAD_SEARCH_URL,
    SALESFORCE_OPPORTUNITY_SEARCH_URL,
    SALESFORCE_ORDER_CREATE_URL,
    SALESFORCE_ORDER_FORM_DATA_URL,
    SALESFORCE_ORDER_LINK_URL,
    SALESFORCE_ORDER_URL,
    SALESFORCE_PRICE_BOOK_PRODUCTS_URL,
    SALESFORCE_RECENT_ORDER_SEARCH_URL,
} from '../../constants/api/salesforce-order-api.constants';
import { Permission } from '../roles.dao';

export const SF_ORDER_TIMEOUT_MS = 10000;
export const SF_ORDER_CREATE_TIMEOUT_MS = 30000;
export const SF_ORDER_FORM_DATA_CACHE_MINUTES = 15;
export const SF_PRICE_BOOK_PRODUCTS_CACHE_MINUTES = 15;

const ORDER_FORM_PRELOAD_PERMISSIONS = [
    Permission.CREATE_ORDER,
    Permission.UPDATE_ORDER,
    Permission.CREATE_ORDER_TEMPLATE,
    Permission.EDIT_ORDER_TEMPLATE,
];

interface ISalesforceOrderCreateResponse {
    success: boolean;
    orderId: string | null;
    oeNumber: string | null;
    message: string | null;
    warningMessage?: string | null;
}

// Lookup data is account-scoped, so cache it per account.
const formDataCacheKey = (accountId: number): string => `salesforce-order-form-data-v4-${accountId}`;
const priceBookProductsCacheKey = (contractId: string | null | undefined, priceBookId: string): string =>
    `salesforce-pricebook-products-${contractId ?? 'none'}-${priceBookId}`;

class SalesforceOrderDAO extends ISalesforceOrderDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly localStorageService: ILocalStorageService,
    ) {
        super();
    }

    public async getOrderForEvent(params: ISalesforceOrderRequestParams): Promise<ISalesforceOrderDTO> {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), SF_ORDER_TIMEOUT_MS);
        const abortFromCaller = (): void => controller.abort();
        if (params.signal) {
            if (params.signal.aborted) {
                controller.abort();
            } else {
                params.signal.addEventListener('abort', abortFromCaller);
            }
        }
        try {
            const response = await this.apiClient.get<ISalesforceOrderDTO>(
                { url: SALESFORCE_ORDER_URL, permission: Permission.VIEW_SALESFORCE_ORDER },
                { accountId: params.accountId, orderId: params.orderId },
                { signal: controller.signal },
            );
            return response.parsedResponse;
        } finally {
            clearTimeout(timeout);
            params.signal?.removeEventListener('abort', abortFromCaller);
        }
    }

    // Cached in localStorage for 15 minutes per account to avoid repeated Salesforce calls.
    public async getOrderFormData(params: ISalesforceOrderFormDataRequestParams): Promise<ISalesforceOrderFormDataDTO> {
        const key = formDataCacheKey(params.accountId);
        const cached = this.localStorageService.getLocalStorageValue<ILocalStorageResponse>(key);
        if (cached && cached.value && this.localStorageService.checkTimeRange(cached.creationDate, key, SF_ORDER_FORM_DATA_CACHE_MINUTES)) {
            return cached.value as ISalesforceOrderFormDataDTO;
        }

        const response = await this.apiClient.get<ISalesforceOrderFormDataDTO>(
            { url: SALESFORCE_ORDER_FORM_DATA_URL, permissions: ORDER_FORM_PRELOAD_PERMISSIONS },
            { accountId: params.accountId },
        );
        const data = response.parsedResponse;
        if (data) {
            const recentOrders = data.recentOrders?.map(order => ({ ...order, requesterName: order.requesterName || null }));
            this.localStorageService.setLocalStorageValue({
                name: key,
                value: recentOrders ? { ...data, recentOrders: recentOrders } : data,
                creationDate: Date.now(),
            });
        }
        return data;
    }

    public async searchAccounts(params: ISalesforceAccountSearchParams): Promise<ISalesforceAccountDTO[]> {
        const response = await this.apiClient.get<ISalesforceAccountSearchDTO>(
            { url: SALESFORCE_ACCOUNT_SEARCH_URL, permissions: ORDER_FORM_PRELOAD_PERMISSIONS },
            { accountId: params.accountId, searchTerm: params.searchTerm },
        );
        return response.parsedResponse?.accounts ?? [];
    }

    public async searchUsers(params: ISalesforceUserSearchParams): Promise<ISalesforceUserDTO[]> {
        const response = await this.apiClient.get<ISalesforceUserSearchDTO>(
            { url: SALESFORCE_BUSINESS_LEAD_SEARCH_URL, permission: Permission.UPDATE_ORDER },
            { accountId: params.accountId, searchTerm: params.searchTerm },
        );
        const data = response.parsedResponse;
        if (data?.available === false) {
            throw new Error(data.message || 'Business lead search is temporarily unavailable.');
        }
        return data?.businessLeads ?? [];
    }

    public async searchOpportunities(params: ISalesforceOpportunitySearchParams): Promise<ISalesforceOpportunityDTO[]> {
        const response = await this.apiClient.get<ISalesforceOpportunitySearchDTO>(
            { url: SALESFORCE_OPPORTUNITY_SEARCH_URL, permissions: ORDER_FORM_PRELOAD_PERMISSIONS },
            { accountId: params.accountId, searchTerm: params.searchTerm },
        );
        return response.parsedResponse?.opportunities ?? [];
    }

    public async searchRecentOrders(params: IRecentOrderSearchParams): Promise<IRecentOrderDTO[]> {
        const response = await this.apiClient.get<IRecentOrderSearchDTO>(
            { url: SALESFORCE_RECENT_ORDER_SEARCH_URL, permission: Permission.CREATE_ORDER },
            { accountId: params.accountId, searchTerm: params.searchTerm },
            params.signal ? { signal: params.signal } : undefined,
        );
        return response.parsedResponse?.recentOrders ?? [];
    }

    public async createOrder(params: ISalesforceOrderCreateParams): Promise<ISalesforceOrderCreateResult> {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), SF_ORDER_CREATE_TIMEOUT_MS);

        const url = SALESFORCE_ORDER_CREATE_URL
            .replace('{accountId}', String(params.accountId))
            .replace('{eventId}', String(params.eventId));
        try {
            const response = await this.apiClient.post<ISalesforceOrderCreateResponse, ISalesforceOrderCreatePayload>(
                { url: url, permission: Permission.CREATE_ORDER },
                params.payload,
                {
                    signal: controller.signal,
                    disableRetry: true,
                },
            );
            const body = response.parsedResponse;
            return {
                success: body?.success ?? false,
                orderId: body?.orderId ?? null,
                orderNumber: body?.oeNumber ?? null,
                errorMessage: body && !body.success ? (body.message ?? null) : null,
                warningMessage: body?.warningMessage ?? null,
            };
        } finally {
            clearTimeout(timeout);
        }
    }

    public async linkOrder(params: ISalesforceOrderLinkParams): Promise<ISalesforceOrderCreateResult> {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), SF_ORDER_CREATE_TIMEOUT_MS);

        const url = SALESFORCE_ORDER_LINK_URL
            .replace('{accountId}', String(params.accountId))
            .replace('{eventId}', String(params.eventId));
        try {
            const response = await this.apiClient.post<ISalesforceOrderCreateResponse, ISalesforceOrderLinkPayload>(
                { url: url, permission: Permission.CREATE_ORDER },
                { orderId: params.orderId, oeNumber: params.orderNumber },
                {
                    signal: controller.signal,
                    disableRetry: true,
                },
            );
            const body = response.parsedResponse;
            return {
                success: body?.success ?? false,
                orderId: body?.orderId ?? params.orderId,
                orderNumber: body?.oeNumber ?? null,
                errorMessage: body && !body.success ? (body.message ?? null) : null,
            };
        } finally {
            clearTimeout(timeout);
        }
    }

    public async updateOrder(params: ISalesforceOrderUpdateParams): Promise<ISalesforceOrderCreateResult> {
        const url = SALESFORCE_ORDER_CREATE_URL
            .replace('{accountId}', String(params.accountId))
            .replace('{eventId}', String(params.eventId));
        const response = await this.apiClient.put<ISalesforceOrderCreateResponse, ISalesforceOrderUpdatePayload>(
            { url: url, permission: Permission.UPDATE_ORDER },
            params.payload,
            { disableRetry: true },
        );
        const body = response.parsedResponse;
        return {
            success: body?.success ?? false,
            orderId: body?.orderId ?? params.orderId,
            orderNumber: body?.oeNumber ?? null,
            errorMessage: body && !body.success ? (body.message ?? null) : null,
            warningMessage: body?.warningMessage ?? null,
        };
    }

    public async getPriceBookProducts(params: ISalesforcePriceBookProductsParams): Promise<ISalesforcePriceBookProductsDTO> {
        const key = priceBookProductsCacheKey(params.contractId, params.priceBookId);
        const cached = this.localStorageService.getLocalStorageValue<ILocalStorageResponse>(key);
        if (cached && cached.value && this.localStorageService.checkTimeRange(cached.creationDate, key, SF_PRICE_BOOK_PRODUCTS_CACHE_MINUTES)) {
            return cached.value as ISalesforcePriceBookProductsDTO;
        }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), SF_ORDER_TIMEOUT_MS);
        try {
            const response = await this.apiClient.get<ISalesforcePriceBookProductsDTO>(
                { url: SALESFORCE_PRICE_BOOK_PRODUCTS_URL, permission: Permission.MANAGE_PRODUCTS },
                { priceBookId: params.priceBookId },
                { signal: controller.signal },
            );
            const data = response.parsedResponse ?? { available: false, products: [], message: null };
            if (data.available) {
                this.localStorageService.setLocalStorageValue({ name: key, value: data, creationDate: Date.now() });
            }
            return data;
        } finally {
            clearTimeout(timeout);
        }
    }
}

export { SalesforceOrderDAO };
